import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REMCARNE_MOVIMIENTOS,
  REMCARNE_MOVIMIENTO_LABELS,
  REMCARNE_RECEPTORES,
  REMCARNE_RECEPTOR_LABELS,
  type RemcarneCatalogsDTO,
  type RemcarneCodeDTO,
  type RemcarneMovimiento,
  type RemcarneProductRowDTO,
  type RemcarneReceptor,
  type WholesaleOrderDTO,
  type WholesaleRemitoDTO,
  type WholesaleSettingsDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Dialog, Field, Input, Select, useToast } from '@bifeai/ui';
import { FileCheck2, Printer, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useWsSettings } from './common';

/**
 * Remito electrónico cárnico (ARCA, RG 4256): el remito legal para trasladar carne. Lo autoriza ARCA con el
 * certificado de la carnicería (el mismo de la factura, asociado también al servicio del remito).
 */

const cuitText = (c: string | null | undefined) => (c && c.length === 11 ? `${c.slice(0, 2)}-${c.slice(2, 10)}-${c.slice(10)}` : (c ?? ''));

/** Tablas de ARCA (categorías, tipos de carne, domicilios): se piden a ARCA cuando se tocan. */
export function useRemcarneCatalogs(enabled: boolean) {
  return useQuery({
    queryKey: ['wholesale', 'remcarne', 'catalogs'],
    queryFn: () => api.get<RemcarneCatalogsDTO>('/wholesale/remcarne/catalogs'),
    enabled,
    staleTime: 60 * 60_000,
    retry: false,
  });
}

function CodeSelect({
  label,
  hint,
  value,
  list,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number | null;
  list: RemcarneCodeDTO[] | undefined;
  disabled?: boolean;
  onChange: (v: number | null) => void;
}) {
  // Sin las tablas de ARCA todavía: se puede escribir el código a mano.
  if (!list?.length)
    return (
      <Field label={label} hint={hint ?? 'Código de la tabla de ARCA.'}>
        <Input
          inputMode="numeric"
          value={value === null ? '' : String(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, '') ? Number(e.target.value.replace(/\D/g, '')) : null)}
        />
      </Field>
    );
  return (
    <Field label={label} hint={hint}>
      <Select
        value={value === null ? '' : String(value)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">Elegí</option>
        {list.map((c) => (
          <option key={c.codigo} value={c.codigo}>
            {c.codigo} · {c.descripcion}
          </option>
        ))}
      </Select>
    </Field>
  );
}

// ================================================================ configuración
/** Configurar mayorista → Remito cárnico: datos del emisor y tipo de carne de cada producto. */
export function RemcarneSettingsCard({
  v,
  setV,
  owner,
  onSave,
  saving,
}: {
  v: WholesaleSettingsDTO;
  setV: (s: WholesaleSettingsDTO) => void;
  owner: boolean;
  onSave: () => void;
  saving: boolean;
}) {
  const { me } = useSession();
  const [load, setLoad] = useState(false);
  const cat = useRemcarneCatalogs(load && v.remcarneEnabled);
  const hasArca = me.tenant.modules.includes('ARCA');
  return (
    <Card>
      <CardHeader
        title="Remito electrónico cárnico (ARCA)"
        subtitle="El remito legal para trasladar carne (RG 4256). Lo autoriza ARCA con número, código y QR. Se emite desde cada pedido ya preparado."
      />
      <CardBody>
        {!hasArca ? (
          <Callout tone="info">Necesita la facturación electrónica (ARCA) activa: usa el mismo certificado de la carnicería.</Callout>
        ) : (
          <div className="bf-form">
            <Field
              label="Emitir remitos cárnicos"
              hint="El certificado de ARCA tiene que estar asociado también al servicio «Remito Electrónico Cárnico» (wsremcarne)."
            >
              <Select
                value={v.remcarneEnabled ? '1' : '0'}
                disabled={!owner}
                onChange={(e) => setV({ ...v, remcarneEnabled: e.target.value === '1' })}
              >
                <option value="0">No</option>
                <option value="1">Sí</option>
              </Select>
            </Field>
            {v.remcarneEnabled && (
              <>
                <div>
                  <Button size="sm" icon={<RefreshCw />} loading={cat.isFetching} onClick={() => (load ? void cat.refetch() : setLoad(true))}>
                    Traer las tablas de ARCA
                  </Button>
                  {cat.data && (
                    <span className="bf-cell-sub" style={{ marginLeft: 8 }}>
                      {cat.data.environment === 'HOMO' ? 'Ambiente de pruebas de ARCA (sin validez)' : 'ARCA producción'}
                    </span>
                  )}
                </div>
                {cat.error && <Callout tone="danger">{errorMessage(cat.error)}</Callout>}
                <div className="bf-ws-form">
                  <Field label="Punto de emisión" hint="El que diste de alta en ARCA para el remito cárnico.">
                    <Input
                      inputMode="numeric"
                      value={v.remcarnePuntoEmision ? String(v.remcarnePuntoEmision) : ''}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, remcarnePuntoEmision: Number(e.target.value.replace(/\D/g, '')) || null })}
                    />
                  </Field>
                  <CodeSelect
                    label="Categoría de la carnicería"
                    value={v.remcarneCategoriaEmisor}
                    list={cat.data?.categoriasEmisor}
                    disabled={!owner}
                    onChange={(x) => setV({ ...v, remcarneCategoriaEmisor: x })}
                  />
                  <CodeSelect
                    label="Domicilio de origen"
                    hint="El depósito o local registrado en ARCA de donde sale la carne."
                    value={v.remcarneDomOrigen}
                    list={cat.data?.domicilios}
                    disabled={!owner}
                    onChange={(x) => setV({ ...v, remcarneDomOrigen: x })}
                  />
                  <Field label="Tipo de movimiento">
                    <Select
                      value={v.remcarneTipoMovimiento}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, remcarneTipoMovimiento: e.target.value as RemcarneMovimiento })}
                    >
                      {REMCARNE_MOVIMIENTOS.map((m) => (
                        <option key={m} value={m}>
                          {REMCARNE_MOVIMIENTO_LABELS[m]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Tipo de receptor">
                    <Select
                      value={v.remcarneTipoReceptor}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, remcarneTipoReceptor: e.target.value as RemcarneReceptor })}
                    >
                      {REMCARNE_RECEPTORES.map((m) => (
                        <option key={m} value={m}>
                          {REMCARNE_RECEPTOR_LABELS[m]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="CUIT del transportista" hint="Vacío = transporte propio (el CUIT de la carnicería).">
                    <Input
                      inputMode="numeric"
                      value={v.remcarneCuitTransportista ?? ''}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, remcarneCuitTransportista: e.target.value.replace(/\D/g, '').slice(0, 11) || null })}
                    />
                  </Field>
                  <Field label="CUIT del chofer (por defecto)" hint="Se puede cambiar en cada remito.">
                    <Input
                      inputMode="numeric"
                      value={v.remcarneCuitConductor ?? ''}
                      disabled={!owner}
                      onChange={(e) => setV({ ...v, remcarneCuitConductor: e.target.value.replace(/\D/g, '').slice(0, 11) || null })}
                    />
                  </Field>
                </div>
              </>
            )}
            {owner && (
              <div>
                <Button variant="primary" loading={saving} onClick={onSave}>
                  Guardar
                </Button>
              </div>
            )}
            {v.remcarneEnabled && <RemcarneProductsTable tipos={cat.data?.tiposCarne} owner={owner} />}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Qué tipo de carne de ARCA es cada producto (una vez; después sale solo en cada remito). */
function RemcarneProductsTable({ tipos, owner }: { tipos: RemcarneCodeDTO[] | undefined; owner: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const rows = useQuery({
    queryKey: ['wholesale', 'remcarne', 'products'],
    queryFn: () => api.get<RemcarneProductRowDTO[]>('/wholesale/remcarne/products'),
  });
  const [edits, setEdits] = useState<Record<string, string | null>>({});
  const save = useMutation({
    mutationFn: () =>
      api.put<RemcarneProductRowDTO[]>('/wholesale/remcarne/products', {
        items: Object.entries(edits).map(([productId, codTipoProd]) => ({ productId, codTipoProd })),
      }),
    onSuccess: async () => {
      setEdits({});
      await qc.invalidateQueries({ queryKey: ['wholesale', 'remcarne', 'products'] });
      toast.success('Tipos de carne guardados');
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const list = rows.data ?? [];
  const missing = list.filter((r) => !(edits[r.productId] ?? r.codTipoProd)).length;
  return (
    <div className="bf-field">
      <span className="bf-label">Tipo de carne de ARCA de cada producto</span>
      <span className="bf-hint">
        {tipos?.length ? 'Elegí el tipo de cada corte.' : 'Traé las tablas de ARCA para elegirlo de la lista, o escribí el código (ej. 2.13).'}
        {missing > 0 ? ` Faltan ${missing}: sin el tipo, ese producto no puede ir en un remito.` : ''}
      </span>
      <div style={{ display: 'grid', gap: 6, maxHeight: 360, overflow: 'auto' }}>
        {list.map((r) => {
          const val = edits[r.productId] !== undefined ? edits[r.productId] : r.codTipoProd;
          return (
            <label key={r.productId} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: 8, alignItems: 'center' }}>
              <span>{r.productName}</span>
              {tipos?.length ? (
                <Select value={val ?? ''} disabled={!owner} onChange={(e) => setEdits({ ...edits, [r.productId]: e.target.value || null })}>
                  <option value="">Sin tipo</option>
                  {tipos.map((t) => (
                    <option key={t.codigo} value={t.codigo}>
                      {t.codigo} · {t.descripcion}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={val ?? ''}
                  disabled={!owner}
                  placeholder="2.13"
                  onChange={(e) => setEdits({ ...edits, [r.productId]: e.target.value.trim() || null })}
                />
              )}
            </label>
          );
        })}
      </div>
      {owner && Object.keys(edits).length > 0 && (
        <div>
          <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Guardar tipos de carne
          </Button>
        </div>
      )}
    </div>
  );
}

// ================================================================ ficha del cliente
/** En la ficha del cliente mayorista: su categoría y su domicilio en ARCA (solo si se emiten remitos cárnicos). */
export function RemcarneCustomerFields({
  categoria,
  domicilio,
  onChange,
}: {
  categoria: number | null;
  domicilio: number | null;
  onChange: (v: { remcarneCategoria: number | null; remcarneDomDestino: number | null }) => void;
}) {
  const settings = useWsSettings();
  const [load, setLoad] = useState(false);
  const cat = useRemcarneCatalogs(load);
  if (!settings.data?.remcarneEnabled) return null;
  return (
    <div className="bf-field" style={{ gridColumn: '1 / -1' }}>
      <span className="bf-label">Remito cárnico (ARCA)</span>
      <div className="bf-ws-form">
        <CodeSelect
          label="Categoría del cliente"
          value={categoria}
          list={cat.data?.categoriasReceptor}
          onChange={(x) => onChange({ remcarneCategoria: x, remcarneDomDestino: domicilio })}
        />
        <Field label="Código de domicilio en ARCA" hint="Solo si el cliente tiene su local registrado en ARCA (si no, vacío).">
          <Input
            inputMode="numeric"
            value={domicilio === null ? '' : String(domicilio)}
            onChange={(e) =>
              onChange({
                remcarneCategoria: categoria,
                remcarneDomDestino: e.target.value.replace(/\D/g, '') ? Number(e.target.value.replace(/\D/g, '')) : null,
              })
            }
          />
        </Field>
      </div>
      {!cat.data && (
        <div>
          <Button size="sm" icon={<RefreshCw />} loading={cat.isFetching} onClick={() => setLoad(true)}>
            Ver las categorías de ARCA
          </Button>
        </div>
      )}
      {cat.error && <Callout tone="danger">{errorMessage(cat.error)}</Callout>}
    </div>
  );
}

// ================================================================ pedido
type ForOrder = { remitos: WholesaleRemitoDTO[]; defaults: { cuitConductor: string | null; dominio: string | null } };

/** En el detalle del pedido: el remito cárnico emitido (para imprimir) o el botón para emitirlo. */
export function OrderRemitoBlock({ order }: { order: WholesaleOrderDTO }) {
  const settings = useWsSettings();
  const { can } = useSession();
  const enabled = !!settings.data?.remcarneEnabled && can('wholesale.manage');
  const weighed = ['READY', 'ON_ROUTE', 'DELIVERED', 'SETTLED'].includes(order.status);
  const data = useQuery({
    queryKey: ['wholesale', 'remitos', order.id],
    queryFn: () => api.get<ForOrder>(`/wholesale/orders/${order.id}/remitos`),
    enabled: enabled && weighed,
  });
  const [open, setOpen] = useState(false);
  if (!enabled || !weighed) return null;
  const ok = data.data?.remitos.find((r) => r.status === 'OK');
  const lastError = !ok ? data.data?.remitos.find((r) => r.status === 'ERROR') : undefined;
  return (
    <div className="bf-field">
      <span className="bf-label">Remito electrónico cárnico</span>
      {ok ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge tone="success">
            N° {String(ok.puntoEmision).padStart(5, '0')}-{String(ok.nroRemito).padStart(8, '0')}
          </Badge>
          {ok.environment === 'HOMO' && <Badge tone="warning">Prueba (sin validez)</Badge>}
          <span className="bf-cell-sub">Vence {ok.fechaVencimiento ?? '—'}</span>
          <Button size="sm" icon={<Printer />} onClick={() => printRemito(ok)}>
            Imprimir remito
          </Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {lastError && <Callout tone="danger">El último intento no salió: {lastError.error}</Callout>}
          <div>
            <Button size="sm" icon={<FileCheck2 />} onClick={() => setOpen(true)} disabled={!data.data}>
              Emitir remito ARCA
            </Button>
          </div>
        </div>
      )}
      {open && data.data && <EmitDialog order={order} defaults={data.data.defaults} onClose={() => setOpen(false)} />}
    </div>
  );
}

function EmitDialog({ order, defaults, onClose }: { order: WholesaleOrderDTO; defaults: ForOrder['defaults']; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const lines = order.items.filter((i) => (i.qtyReal ?? 0) > 0);
  const [f, setF] = useState({ cuitConductor: defaults.cuitConductor ?? '', dominio: defaults.dominio ?? '', distanciaKm: '' });
  const [items, setItems] = useState<Record<string, { unidades: string; tropa: string }>>(
    Object.fromEntries(lines.map((i) => [i.id, { unidades: '1', tropa: '' }])),
  );
  const emit = useMutation({
    mutationFn: () =>
      api.post<WholesaleRemitoDTO>(`/wholesale/orders/${order.id}/remitos`, {
        cuitConductor: f.cuitConductor,
        dominio: f.dominio,
        distanciaKm: Number(f.distanciaKm) || 0,
        items: lines.map((i) => ({
          itemId: i.id,
          unidades: Number(items[i.id]?.unidades) || 1,
          tropa: items[i.id]?.tropa ? Number(items[i.id]!.tropa) : null,
        })),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['wholesale', 'remitos', order.id] });
      toast.success('Remito autorizado por ARCA', `N° ${String(r.nroRemito ?? '').padStart(8, '0')}`);
      onClose();
      printRemito(r);
    },
    onError: async (e) => {
      await qc.invalidateQueries({ queryKey: ['wholesale', 'remitos', order.id] });
      toast.error('ARCA no autorizó el remito', errorMessage(e));
    },
  });
  return (
    <Dialog
      open
      wide
      onOpenChange={(o) => !o && onClose()}
      title="Emitir remito electrónico cárnico"
      description="Se pide a ARCA con los kilos reales del pedido. Si lo autoriza, se imprime con su número y código."
    >
      <div className="bf-form">
        <div className="bf-ws-form">
          <Field label="CUIT del chofer">
            <Input inputMode="numeric" value={f.cuitConductor} onChange={(e) => setF({ ...f, cuitConductor: e.target.value })} />
          </Field>
          <Field label="Patente de la camioneta">
            <Input value={f.dominio} placeholder="AE482KD" onChange={(e) => setF({ ...f, dominio: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Kilómetros del viaje">
            <Input inputMode="numeric" value={f.distanciaKm} onChange={(e) => setF({ ...f, distanciaKm: e.target.value.replace(/\D/g, '') })} />
          </Field>
        </div>
        <div className="bf-field">
          <span className="bf-label">Mercadería</span>
          <span className="bf-hint">Unidades: piezas o cajas. Tropa: el número de tropa de faena, si corresponde.</span>
          {lines.map((i) => (
            <div key={i.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) 1fr 1fr', gap: 8, alignItems: 'end' }}>
              <span>
                {i.productName} ·{' '}
                <b>
                  {((i.qtyReal ?? 0) / (i.unitType === 'WEIGHT' ? 1000 : 1)).toLocaleString('es-AR')} {i.unitType === 'WEIGHT' ? 'kg' : 'u.'}
                </b>
              </span>
              <Field label="Unidades">
                <Input
                  inputMode="numeric"
                  value={items[i.id]?.unidades ?? '1'}
                  onChange={(e) => setItems({ ...items, [i.id]: { ...items[i.id]!, unidades: e.target.value.replace(/\D/g, '') } })}
                />
              </Field>
              <Field label="Tropa">
                <Input
                  inputMode="numeric"
                  value={items[i.id]?.tropa ?? ''}
                  onChange={(e) => setItems({ ...items, [i.id]: { ...items[i.id]!, tropa: e.target.value.replace(/\D/g, '') } })}
                />
              </Field>
            </div>
          ))}
        </div>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={emit.isPending} onClick={() => emit.mutate()}>
            Pedir a ARCA
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ================================================================ impresión
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** El QR de ARCA: si viene como imagen se muestra; si viene como dirección, se imprime el texto. */
function qrHtml(qr: string | null): string {
  if (!qr) return '';
  if (/^iVBOR/.test(qr)) return `<img class="qr" src="data:image/png;base64,${esc(qr)}" alt="QR">`;
  if (/^\/9j\//.test(qr)) return `<img class="qr" src="data:image/jpeg;base64,${esc(qr)}" alt="QR">`;
  return `<div class="qrtext">${esc(qr)}</div>`;
}

/** Imprime el remito tal como lo autorizó ARCA (sin marcas del sistema). */
export function printRemito(r: WholesaleRemitoDTO): void {
  const w = window.open('', '_blank', 'width=820,height=980');
  if (!w) return;
  const d = r.detail;
  const rows = d.items
    .map(
      (i) =>
        `<tr><td>${i.orden}</td><td>${esc(i.productName)}</td><td>${esc(i.codTipoProd)}</td><td>${i.tropa ?? ''}</td><td class="r">${i.unidades}</td><td class="r">${i.kilos}</td></tr>`,
    )
    .join('');
  const nro = `${String(r.puntoEmision).padStart(5, '0')}-${String(r.nroRemito ?? 0).padStart(8, '0')}`;
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Remito electrónico cárnico ${nro}</title>
<style>
@page{size:A4;margin:14mm}body{font:12.5px system-ui,sans-serif;color:#111;margin:0}
.box{border:1px solid #333;padding:10px}.top{display:grid;grid-template-columns:1fr auto 1fr;gap:12px;align-items:start}
.issuer{font-size:16px;font-weight:700}.meta{text-align:right}.title{font-size:15px;font-weight:700}
.code{border:1px solid #333;width:74px;text-align:center;padding:4px}.code b{display:block;font-size:22px}.code small{font-size:9px}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:0}.grid>div{border:1px solid #333;border-top:0;padding:8px 10px}
table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #333;padding:5px 6px;text-align:left}th{background:#f2f2f2}.r{text-align:right}
.auth{display:flex;gap:16px;align-items:center;justify-content:space-between;margin-top:14px;border:1px solid #333;padding:10px}
.qr{width:120px;height:120px}.qrtext{font-size:10px;word-break:break-all;max-width:320px}
.test{border:2px dashed #b45309;color:#b45309;text-align:center;font-weight:700;padding:6px;margin-bottom:8px}
.sign{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-top:40px}.sign div{border-top:1px solid #333;padding-top:4px;text-align:center;font-size:11px}
</style></head><body>
${r.environment === 'HOMO' ? '<div class="test">PRUEBA EN EL AMBIENTE DE TESTEO DE ARCA — SIN VALIDEZ</div>' : ''}
<div class="box top">
  <div><div class="issuer">${esc(d.emisor.name)}</div><div>CUIT ${cuitText(d.emisor.cuit)}</div>${d.emisor.address ? `<div>${esc(d.emisor.address)}</div>` : ''}</div>
  <div class="code"><b>R</b><small>Cód. ${r.tipoComprobante}</small></div>
  <div class="meta"><div class="title">Remito electrónico cárnico</div><div>N° ${nro}</div><div>Fecha ${esc(r.fechaEmision ?? d.viaje.fecha)}</div></div>
</div>
<div class="grid">
  <div><b>Destinatario:</b> ${esc(d.receptor.name)}<br>CUIT ${cuitText(d.receptor.cuit)}${d.receptor.address ? `<br>${esc(d.receptor.address)}` : ''}</div>
  <div><b>Transporte:</b> CUIT ${cuitText(d.viaje.cuitTransportista)}<br>Chofer CUIT ${cuitText(d.viaje.cuitConductor)}<br>Patente ${esc(d.viaje.dominio)} · ${d.viaje.distanciaKm} km · Inicio ${esc(d.viaje.fecha)}</div>
</div>
<table><thead><tr><th>#</th><th>Producto</th><th>Tipo ARCA</th><th>Tropa</th><th class="r">Unidades</th><th class="r">Kilos</th></tr></thead><tbody>${rows}</tbody></table>
<div class="auth">
  <div><div><b>Código de autorización:</b> ${esc(r.codAutorizacion ?? '—')}</div><div><b>Vencimiento:</b> ${esc(r.fechaVencimiento ?? '—')}</div><div><b>Código de remito:</b> ${r.codRemito ?? '—'}</div></div>
  ${qrHtml(r.qr)}
</div>
<div class="sign"><div>Firma del chofer</div><div>Recibí conforme · Firma</div><div>Aclaración y DNI</div></div>
<script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
</body></html>`);
  w.document.close();
}
